import { RankKit } from "@/components/site/RankKit";
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { partnerBySlug } from "@/data/partners";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroLlcJourney from "@/assets/hero/hero-llc-journey.jpg";

type Investor = "us" | "nonus";

type PhaseLink = { to: string; label: string };

type Phase = {
  n: number;
  short: string;
  title: string;
  why: string;
  steps: string[];
  /** Replaces `steps` when the non-US path is selected. */
  nonUsSteps?: string[];
  links: PhaseLink[];
  /** Phase only applies to non-US investors (ITIN); hidden on the US path. */
  nonUsOnly?: boolean;
  note?: string;
};

const PHASES: Phase[] = [
  {
    n: 1,
    short: "Holding LLC + EIN",
    title: "Form your holding LLC and request the EIN",
    why: "Everything later in the journey — the bank account, the property LLC, the loan — is issued to an entity, so the entity comes first. Our loans are business purpose and vest in an entity, never in your personal name.",
    steps: [
      "Many investors form a holding LLC in Wyoming or Delaware. Your attorney or CPA should confirm which state fits your situation.",
      "Sign an operating agreement that lists members and ownership percentages.",
      "Request the EIN from the IRS. A formation partner can file it for you so it is issued alongside the formation documents; IRS processing times vary.",
      "Keep the EIN confirmation letter — nearly every later phase asks for it.",
    ],
    links: [
      { to: "/business-setup/llc", label: "LLC formation and EIN" },
    ],
  },
  {
    n: 2,
    nonUsOnly: true,
    short: "ITIN verification",
    title: "Get your ITIN and verify your identity",
    why: "Banks, title companies, and lenders must verify every beneficial owner. If you are investing without a Social Security number, an ITIN is your US tax identity for rental income filings, and many banks and title companies ask for it before they will open an account or complete escrow paperwork.",
    steps: [
      "Apply for an ITIN, which is used for US tax filings on rental income.",
      "A Certifying Acceptance Agent can verify your passport so you do not have to mail the original overseas.",
      "Apply once the EIN is issued so your personal tax identity and the entity line up. ITIN processing usually takes the longest, so start early.",
    ],
    links: [
      { to: "/business-setup/itin", label: "Apply for an ITIN" },
      { to: "/foreign-national-loans", label: "Foreign national financing" },
    ],
  },
  {
    n: 3,
    short: "Pre-qualification",
    title: "Pre-qualify and choose your property type",
    why: "Knowing how a lender will size the loan before you shop keeps you from making offers that will not cash flow. Our programs look at the property's rent compared with its payment rather than your personal income.",
    steps: [
      "Share your target market, price range, down payment, and reserves with a licensed loan officer.",
      "Pick the strategy that fits: long-term rental, Section 8, short-term rental, 2–4 unit, 5+ unit multifamily, or a bridge / fix-and-flip.",
      "Run the numbers with our DSCR calculator to see how rent and payment compare.",
    ],
    nonUsSteps: [
      "Share your target market, price range, down payment, and reserves with a licensed loan officer.",
      "Pick the strategy that fits: long-term rental, Section 8, short-term rental, 2–4 unit, 5+ unit multifamily, or a bridge / fix-and-flip.",
      "Foreign national programs are available on eligible property types; down payment and reserve requirements are typically higher.",
      "Run the numbers with our DSCR calculator to see how rent and payment compare.",
    ],
    links: [
      { to: "/apply", label: "Get Your Personalized Rate Quote" },
      { to: "/rates", label: "See Today's Rates" },
    ],
    note: "A pre-qualification is not a loan approval or commitment to lend. All terms are subject to underwriting approval.",
  },
  {
    n: 4,
    short: "Business bank account",
    title: "Open the business bank account",
    why: "Earnest money, reserves, closing funds, and rent should all move through an account in the entity's name. Underwriting reads those statements, and funds wired from a personal account can slow down escrow.",
    steps: [
      "Bring the formation documents, EIN letter, operating agreement, and ID for each owner.",
      "Fund the account and let the money season — this is where your reserves will be verified.",
    ],
    nonUsSteps: [
      "Bring the formation documents, EIN letter, operating agreement, passport, and ITIN (or proof it has been applied for).",
      "Some banks offer accounts for non-US owners without an in-person visit; requirements vary by bank.",
      "Fund the account and let the money season — this is where your reserves will be verified.",
    ],
    links: [{ to: "/business-setup/business-bank-account", label: "Open business banking" }],
  },
  {
    n: 5,
    short: "Local property LLC",
    title: "Form the local property LLC",
    why: "Many investors hold each property in its own LLC formed in the state where the property sits, owned by the holding LLC. This keeps each property's records separate and avoids registering the holding company as a foreign entity in every state.",
    steps: [
      "Form the property LLC in the property's state, with the holding LLC listed as its member.",
      "Get a separate EIN and, if needed, a separate bank account for the property LLC.",
      "Make sure the purchase contract, title, insurance, and loan documents all read this same entity name.",
    ],
    links: [
      { to: "/business-setup/llc", label: "Form the property LLC" },
      { to: "/business-setup/duns-number", label: "Register a DUNS number" },
    ],
    note: "How much liability protection an entity structure provides depends on state law and how the entities are run. Ask your attorney before relying on it.",
  },
  {
    n: 6,
    short: "Acquisition + closing",
    title: "Make the offer, close, and start renting",
    why: "With the entities, banking, and identity in place, the file is ready to move. The details at this stage decide whether you close on time.",
    steps: [
      "Sign the purchase contract in the property LLC's name (or assign it before closing).",
      "Open title and escrow with the property LLC as buyer and clear any title exceptions.",
      "Bind landlord insurance with the property LLC as named insured and the lender as mortgagee.",
      "Sign the loan documents, fund, and set up rent collection so your next refinance is easy to document.",
    ],
    links: [
      { to: "/business-setup/title-services", label: "Title and escrow" },
      { to: "/business-setup/landlord-insurance", label: "Landlord insurance" },
      { to: "/business-setup/property-management-software", label: "Rent tracking software" },
    ],
  },
];

const FAQS = [
  {
    q: "Why use both a holding LLC and a local property LLC?",
    a: "Many investors keep a holding company in one state and form a separate LLC for each property in the property's state. It keeps records separate and avoids registering one company in every state. Whether it is right for you is a legal and tax question for your attorney or CPA — we are a mortgage broker, not a law or tax firm.",
  },
  {
    q: "Why do people pick Wyoming or Delaware for the holding LLC?",
    a: "Both are commonly chosen for their business-friendly LLC laws and filing options. The best state depends on your situation, so confirm with your attorney or CPA before filing.",
  },
  {
    q: "Can a brand-new LLC with no credit history get a loan?",
    a: "Yes. Our business-purpose programs look at the property's cash flow, and the members typically sign as personal guarantors, so a newly formed entity is normal.",
  },
  {
    q: "Do foreign national investors need the ITIN before applying?",
    a: "Not to start a file, but you will generally need it for US tax filings and many banking relationships, so applying right after the EIN is issued keeps the timeline clean.",
  },
  {
    q: "Do I need the LLC before I make an offer?",
    a: "It is easier if you do. The contract, title, insurance, and loan documents should all read the same entity name. Contracts signed personally can often be assigned, but it adds a step and some sellers resist it.",
  },
];

export const Route = createFileRoute("/business-setup/llc-journey")({
  head: () =>
    buildHead({
      title: "How to Buy an Investment Property in an LLC — Investor Phase Map",
      description:
        "A phase-by-phase map for buying a rental in an LLC: holding LLC and EIN, pre-qualification, business banking, local property LLC, and closing — plus an ITIN identity phase for non-US investors.",
      url: "/business-setup/llc-journey",
      type: "article",
      keywords:
        "how to buy investment property in an LLC, LLC real estate investing step by step, holding LLC and property LLC, Wyoming holding LLC rental property, Delaware LLC real estate investor, form LLC and EIN for rental property, ITIN for foreign national investors, business bank account for LLC, DSCR loan LLC checklist, foreign national investor LLC steps, entity vesting mortgage closing checklist",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "Buying in an LLC: 6-phase map", url: "/business-setup/llc-journey" },
      ],
      service: { name: "LLC Investment Property Journey Guidance" },
      faqs: FAQS.map((f) => ({ q: f.q, a: f.a })),
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to buy an investment property in an LLC",
          description:
            "Six phases for investors buying US rental property through an entity: holding LLC and EIN, identity verification, pre-qualification, business banking, local property LLC, and closing.",
          step: PHASES.map((p) => ({
            "@type": "HowToStep",
            position: p.n,
            name: p.title,
            text: p.why,
            url: p.links[0].to,
          })),
        },
      ],
    }),
  component: LlcJourneyPage,
});

function LlcJourneyPage() {
  const [investor, setInvestor] = useState<Investor>("us");
  const nonUs = investor === "nonus";

  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="How to buy an investment property in an LLC"
        subtitle={
          nonUs
            ? "Six phases, in order: form the holding LLC, get your ITIN, pre-qualify, open business banking, form the local property LLC, then close."
            : "Five phases, in order: form the holding LLC, pre-qualify, open business banking, form the local property LLC, then close."
        }
        image={{
          src: heroLlcJourney,
          alt: "LLC formation documents, operating agreement, EIN letter, business bank folder, and house keys on a desk with a rental home in the background — buying investment property in an LLC",
        }}
      />
      <RankKit page="llc-journey" />
      <PageEssentials hideAnswer />

      <Section>
        <SectionHeader eyebrow="Your path" title={nonUs ? "The 6-phase investor map" : "The 5-phase investor map"} />
        <div className="mt-6 inline-flex rounded-lg border border-border bg-card p-1" role="group" aria-label="Choose investor type">
          {(
            [
              ["us", "US investor"],
              ["nonus", "Non-US investor"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={investor === key}
              onClick={() => setInvestor(key)}
              className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                investor === key ? "bg-brand text-brand-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <ol className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {PHASES.filter((p) => !p.nonUsOnly || nonUs).map((p, idx) => (
            <li key={p.n}>
              <a
                href={`#phase-${p.n}`}
                className="flex h-full flex-col rounded-lg border border-border bg-card p-4 transition-colors hover:border-brand"
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Phase {idx + 1}</span>
                <span className="mt-1 text-sm font-semibold text-foreground">{p.short}</span>
              </a>
            </li>
          ))}
        </ol>
      </Section>

      <Section>
        <SectionHeader eyebrow="Phase by phase" title="What happens in each phase and why it comes when it does" />
        <div className="mt-8 space-y-6">
          {PHASES.filter((p) => !p.nonUsOnly || nonUs).map((p, idx) => {
            const steps = nonUs && p.nonUsSteps ? p.nonUsSteps : p.steps;
            const links = p.links;
            return (
              <div key={p.n} id={`phase-${p.n}`} className="scroll-mt-24 rounded-xl border border-border bg-card p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold text-brand-foreground">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phase {idx + 1}</div>
                    <h3 className="mt-1 font-display text-lg font-bold text-brand">{p.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground">
                      <strong className="text-foreground">Why it matters: </strong>
                      {p.why}
                    </p>
                    <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                      {steps.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                    {p.note && <p className="mt-3 text-xs italic text-muted-foreground">{p.note}</p>}
                    <div className="mt-5 flex flex-wrap gap-3">
                      {links.map((l, i) => (
                        <Link
                          key={l.to + l.label}
                          to={l.to}
                          search={l.to === "/apply" ? { product: "dscr" } : undefined}
                          className={
                            i === 0
                              ? "inline-flex rounded-md bg-brand-gold px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-brand-gold-hover"
                              : "inline-flex rounded-md border border-brand px-4 py-2 text-sm font-semibold text-brand"
                          }
                        >
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <PartnerCTA
          partner={partnerBySlug("doola")!}
          note="Not formed yet? Start at phase one — entity formation, registered agent, EIN filing, and operating agreement, including formations for non-US founders."
        />
      </Section>

      <Section>
        <SectionHeader eyebrow="Questions investors ask" title="LLC purchase journey FAQs" />
        <div className="mt-6 space-y-4">
          {FAQS.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <div className="font-display text-base font-bold text-brand">{f.q}</div>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-xs text-muted-foreground">
          Simply Approved Mortgages is a mortgage broker offering business-purpose financing for investment properties. We do not
          provide legal, tax, or accounting advice. Entity formation, state selection, and tax identification decisions should be
          made with a licensed attorney or CPA. Pre-qualification is not a commitment to lend; all loans are subject to
          underwriting approval.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/business-setup" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">
            Business Setup hub
          </Link>
          <Link to="/partners" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">
            See all partners
          </Link>
        </div>
        <PartnerDisclosure />
      </Section>
      <PageOutro />
    </SiteShell>
  );
}
